// Cadastro de um colaborador: dados pessoais/contato editáveis e os vínculos (um por admissão),
// cada um editado num modal com remuneração, benefícios, dados do PJ e desligamento.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, FileText, Pencil, Plus, Save, Undo2, UserMinus } from "lucide-react";
import {
  deleteRhDesligamento, getRhColaborador, getRhOpcoes, postRhContrato, postRhGerarTermo, putRhColaborador, putRhContrato,
} from "../../api";
import { formatData } from "../estoque/comum";
import { AvisoErro, BadgeAtivo, Modal, btnStyle, palette } from "../estoque/ui";
import { useFormatValor } from "./valores";
import DesligamentoModal from "./DesligamentoModal";
import RhLayout from "./RhLayout";
import {
  FormContato, FormContrato, FormPessoa, Secao, contatoParaApi, contatoParaForm, contratoParaApi, contratoParaForm,
  pessoaParaApi, pessoaParaForm,
} from "./formularios";

function Info({ C, rotulo, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>{rotulo}</div>
      <div style={{ fontSize: 13, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>{children || "—"}</div>
    </div>
  );
}

function CartaoVinculo({ C, contrato, onEditar, onHistorico, onDesligar, onGerarTermo, onDesfazer }) {
  const formatBRL = useFormatValor();
  const deslig = contrato.desligamento;
  const periodo = `${formatData(contrato.data_admissao)} → ${contrato.data_desligamento ? formatData(contrato.data_desligamento) : "atual"}`;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, background: contrato.status === "Ativo" ? C.card : C.rowAlt }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <strong style={{ fontSize: 15 }}>{contrato.tipo_contratacao || "Sem tipo"}</strong>
            <BadgeAtivo C={C} ativo={contrato.status === "Ativo"} />
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{periodo}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {contrato.status === "Ativo" && <button onClick={onDesligar} style={btnStyle(C.danger, true)}><UserMinus size={14} /> Desligar</button>}
          <button onClick={onEditar} style={btnStyle(C.brand, true)}><Pencil size={14} /> Editar</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, marginTop: 12 }}>
        <Info C={C} rotulo="Empresa">{contrato.empresa || contrato.centro_custo}</Info>
        <Info C={C} rotulo="Departamento">{contrato.unidade}</Info>
        <Info C={C} rotulo="Cargo">{[contrato.cargo, contrato.nivel].filter(Boolean).join(" · ")}</Info>
        <Info C={C} rotulo="Modalidade">{contrato.modalidade_trabalho}</Info>
        <Info C={C} rotulo="Prestação / salário">{contrato.valor_prestacao_servico !== null ? formatBRL(contrato.valor_prestacao_servico) : null}</Info>
        <Info C={C} rotulo="Benefícios / mês">{contrato.beneficios.length ? formatBRL(contrato.total_beneficios) : null}</Info>
        {contrato.tipo_contratacao === "PJ" && <Info C={C} rotulo="CNPJ">{contrato.cnpj_prestador}</Info>}
        {contrato.tipo_contratacao === "PJ" && <Info C={C} rotulo="Razão social">{contrato.pj?.razao_social}</Info>}
        {contrato.data_desligamento && <Info C={C} rotulo="Desligamento">{[contrato.tipo_desligamento, contrato.motivo_desligamento].filter(Boolean).join(" · ")}</Info>}
      </div>
      {deslig && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: `${C.danger}0F`, border: `1px solid ${C.border}`, fontSize: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <strong>Desligamento registrado na plataforma · {deslig.categoria}</strong>
            <span style={{ display: "flex", gap: 10 }}>
              {deslig.termo_url && <a href={deslig.termo_url} target="_blank" rel="noreferrer" style={{ color: C.brand, fontWeight: 700, display: "inline-flex", gap: 4, alignItems: "center" }}><ExternalLink size={12} /> Termo de rescisão</a>}
              {!deslig.termo_url && contrato.tipo_contratacao === "PJ" && <button onClick={onGerarTermo} style={{ background: "none", border: "none", color: C.brand, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>Gerar termo</button>}
              <button onClick={onDesfazer} title="Desfazer um desligamento registrado por engano" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontFamily: "inherit", padding: 0, display: "inline-flex", gap: 4, alignItems: "center" }}><Undo2 size={12} /> Desfazer</button>
            </span>
          </div>
          <div style={{ color: C.muted, marginTop: 4 }}>
            {[deslig.iniciativa === "colaborador" ? "Iniciativa do colaborador" : "Iniciativa da empresa",
              deslig.valor_rescisao !== null && `Valor ${formatBRL(deslig.valor_rescisao)}`,
              deslig.equipamento_devolver && `Devolver: ${deslig.descricao_equipamento}`, deslig.observacao].filter(Boolean).join(" · ")}
          </div>
        </div>
      )}
      {contrato.tipo_contratacao === "PJ" && (
        <button onClick={onHistorico} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: C.brand, marginTop: 10, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
          <FileText size={13} /> Histórico e aditivos ({contrato.qtd_movimentacoes} movimentação(ões), {contrato.qtd_documentos} documento(s))
        </button>
      )}
    </div>
  );
}

function ModalVinculo({ C, contrato, opcoes, onClose, onSalvar }) {
  const [form, setForm] = useState(() => contratoParaForm(contrato || {}));
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!form.tipo_contratacao || !form.data_admissao) {
      setErro("Preencha tipo de contratação e data de admissão.");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      await onSalvar(contratoParaApi(form));
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  return (
    <Modal C={C} titulo={contrato ? "Editar vínculo" : "Novo vínculo"} onClose={onClose} largura={960}
      rodape={<>
        <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
        <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>
          <Save size={15} /> {salvando ? "Salvando…" : "Salvar"}
        </button>
      </>}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <FormContrato C={C} form={form} setForm={setForm} opcoes={opcoes} />
    </Modal>
  );
}

export default function ColaboradorView() {
  const { id } = useParams();
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const [detalhe, setDetalhe] = useState(null);
  const [opcoes, setOpcoes] = useState(null);
  const [pessoa, setPessoa] = useState(null);
  const [contato, setContato] = useState(null);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [modal, setModal] = useState(null); // null | "novo" | contrato
  const [desligando, setDesligando] = useState(null); // contrato

  const aplicar = useCallback((d) => {
    setDetalhe(d);
    setPessoa(pessoaParaForm(d.pessoa));
    setContato(contatoParaForm(d.contato));
  }, []);

  useEffect(() => {
    setErro("");
    Promise.all([getRhColaborador(id), getRhOpcoes()])
      .then(([d, o]) => { aplicar(d); setOpcoes(o); })
      .catch((e) => setErro(e.message));
  }, [id, aplicar]);

  const alterado = detalhe && pessoa && (
    JSON.stringify(pessoa) !== JSON.stringify(pessoaParaForm(detalhe.pessoa)) ||
    JSON.stringify(contato) !== JSON.stringify(contatoParaForm(detalhe.contato))
  );

  async function salvarDados() {
    setSalvando(true);
    setErro("");
    setAviso("");
    try {
      aplicar(await putRhColaborador(id, { pessoa: pessoaParaApi(pessoa), contato: contatoParaApi(contato) }));
      setAviso("Dados salvos.");
    } catch (e) {
      setErro(e.data?.mensagem || e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function salvarVinculo(dados) {
    const d = modal === "novo" ? await postRhContrato(id, dados) : await putRhContrato(modal.id, dados);
    aplicar(d);
    setModal(null);
    setAviso("Vínculo salvo.");
  }

  const recarregar = () => getRhColaborador(id).then(aplicar);

  async function gerarTermo(desligamento) {
    setErro("");
    setAviso("");
    try {
      const doc = await postRhGerarTermo(desligamento.id);
      await recarregar();
      setAviso(`${doc.nome} gerado.`);
    } catch (e) {
      setErro(e.message);
    }
  }

  async function desfazer(desligamento) {
    if (!window.confirm("Desfazer o desligamento? O vínculo volta a ficar ativo. O termo já gerado continua no Drive.")) return;
    setErro("");
    try {
      await deleteRhDesligamento(desligamento.id);
      await recarregar();
      setAviso("Desligamento desfeito — vínculo ativo de novo.");
    } catch (e) {
      setErro(e.message);
    }
  }

  const atual = detalhe?.contratos.find((c) => c.status === "Ativo") || detalhe?.contratos[0];

  return (
    <RhLayout titulo={detalhe ? detalhe.pessoa.nome : "Colaborador"} isLight={isLight} setIsLight={setIsLight}>
      <div style={{ maxWidth: 1100 }}>
        <button onClick={() => navigate("/rh/colaboradores")} style={{ ...btnStyle(C.muted, true), marginBottom: 12 }}>
          <ArrowLeft size={15} /> Colaboradores
        </button>
        <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
        {aviso && <div role="status" style={{ background: `${C.success}18`, border: `1px solid ${C.success}`, color: C.success, borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>{aviso}</div>}

        {!detalhe || !opcoes ? (
          !erro && <div style={{ color: C.muted, fontSize: 13 }}>Carregando…</div>
        ) : (
          <>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{detalhe.pessoa.nome}</h1>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
                  {[atual?.tipo_contratacao, atual?.empresa, atual?.unidade, atual?.cargo].filter(Boolean).join(" · ") || "Sem vínculo"}
                </div>
              </div>
              {atual && <BadgeAtivo C={C} ativo={atual.status === "Ativo"} />}
            </div>

            <Secao C={C} titulo={`Vínculos (${detalhe.contratos.length})`} descricao="Um por admissão — efetivação ou mudança de regime gera um novo vínculo"
              acoes={<button onClick={() => setModal("novo")} style={btnStyle(C.brand)}><Plus size={15} /> Novo vínculo</button>}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {detalhe.contratos.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>Nenhum vínculo cadastrado.</div>}
                {detalhe.contratos.map((c) => (
                  <CartaoVinculo key={c.id} C={C} contrato={c} onEditar={() => setModal(c)}
                    onHistorico={() => navigate(`/rh/contratos-pj?contrato=${c.id}`)} onDesligar={() => setDesligando(c)}
                    onGerarTermo={() => gerarTermo(c.desligamento)} onDesfazer={() => desfazer(c.desligamento)} />
                ))}
              </div>
            </Secao>

            <Secao C={C} titulo="Dados pessoais"><FormPessoa C={C} form={pessoa} setForm={setPessoa} /></Secao>
            <Secao C={C} titulo="Contato e endereço"><FormContato C={C} form={contato} setForm={setContato} /></Secao>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 24, position: "sticky", bottom: 12 }}>
              {alterado && <button onClick={() => aplicar(detalhe)} style={{ ...btnStyle(C.muted, true), background: C.card }}>Descartar</button>}
              <button onClick={salvarDados} disabled={!alterado || salvando} style={{ ...btnStyle(C.brand), opacity: !alterado || salvando ? 0.5 : 1 }}>
                <Save size={15} /> {salvando ? "Salvando…" : "Salvar dados pessoais"}
              </button>
            </div>
          </>
        )}
      </div>

      {desligando && (
        <DesligamentoModal C={C} contrato={desligando} nome={detalhe.pessoa.nome} onClose={() => setDesligando(null)}
          onConcluido={async (r) => {
            setDesligando(null);
            await recarregar();
            setAviso(`Desligamento registrado (${r.tipo.toLowerCase()}).${r.documento ? ` ${r.documento.nome} gerado.` : ""}`);
          }} />
      )}
      {modal && (
        <ModalVinculo C={C} contrato={modal === "novo" ? null : modal} opcoes={opcoes} onClose={() => setModal(null)} onSalvar={salvarVinculo} />
      )}
    </RhLayout>
  );
}
